import { useMemo } from 'react';

import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';

type IDecisionErknmSubjectBaseFieldPaths = {
  [K in keyof IDecisionOrganizationForm]-?: string;
};

interface IDecisionErknmSubjectFieldPathsExtended {
  organizationGuid: string;
  firstOrganizationName: string;
  dateCreate: string;
  location: string;
  isFiz: string;
}

export interface IDecisionErknmSubjectFieldPaths
  extends IDecisionErknmSubjectBaseFieldPaths,
    IDecisionErknmSubjectFieldPathsExtended {}

type IBaseKeys = keyof IDecisionErknmSubjectBaseFieldPaths;
type IExtendedKeys = keyof IDecisionErknmSubjectFieldPathsExtended;

type IDuplicateKeys = IExtendedKeys & IBaseKeys;

// проверка на дублирование полей из IBaseKeys в IExtendedKeys, дублирующиеся поля будут в IDuplicateKeys
const isAssertNoDuplicates: IDuplicateKeys extends never ? true : false = true;

// eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
isAssertNoDuplicates;

// объект для генерации списка полей в рантайме
const FIELD_KEYS: Record<keyof IDecisionErknmSubjectFieldPaths, true> = {
  // Base fields (from IKnmErknmFormSubject)
  inn: true,
  ogrn: true,
  mspCode: true,
  mspRegistryDate: true,
  checkSubjectResult: true,
  isAutocomplete: true,
  guid: true,
  isNew: true,
  isEmpty: true,
  nkoForeignAgentsRegistryNumber: true,
  nkoForeignAgentsRegistryInclusionDate: true,
  nkoForeignAgentsRegistryExclusionDate: true,
  isNko: true,
  kpp: true,
  smevRequestStatus: true,
  rafpNza: true,
  country: true,
  isSonko: true,
  locationAddress: true,
  workAddress: true,
  factualAddress: true,
  snils: true,
  document: true,
  epguNotifications: true,
  ervkId: true,
  isPresentInItRegistry: true,
  itAccreditationResponse: true,
  smevItAccreditationRequestStatus: true,
  locationregion: true,
  locationnotInFIAS: true,
  locationmanualAddress: true,
  locationfiasRegion: true,
  locationisErrorFiasValue: true,
  locationRegionCodeFromFns: true,
  originalNameReactQuillEditor: true,
  organizationType: true,
  originalName: true,
  name: true,
  isForeignSubjectAgencyNotRegisteredInRf: true,
  resultDecisions: true,
  act: true,

  // Extended fields
  organizationGuid: true,
  firstOrganizationName: true,
  dateCreate: true,
  location: true,
  isFiz: true,
};

interface IUseDecisionErknmSubjectFieldsProps {
  name: string | undefined;
}

/**
 * хук, который генерирует согласованный набор путей к полям формы.
 *
 * @example
 * const subjectFieldNames = useDecisionErknmSubjectFields({ name: 'legalEntity' });
 * <SelectInputField id={subjectFieldNames.subjectType} name={subjectFieldNames.subjectType}/>
 *
 * @param {IUseDecisionErknmSubjectFieldsProps} props .
 * @returns {DecisionErknmSubjectFieldPaths} Объект, сопоставляющий логические имена полей с их полными путями в форме.
 */
export const useDecisionErknmSubjectFields = ({
  name,
}: IUseDecisionErknmSubjectFieldsProps): IDecisionErknmSubjectFieldPaths => {
  return useMemo(() => {
    const prefix = name ?? '';

    const rawFields = {} as IDecisionErknmSubjectFieldPaths;

    (Object.keys(FIELD_KEYS) as Array<keyof IDecisionErknmSubjectFieldPaths>).forEach(key => {
      rawFields[key] = `${prefix}.${key}`;
    });

    return rawFields;
  }, [name]);
};
